import { useEffect, useState } from "react";
import axios from "axios";
import { Check, Loader2, Clock, ChefHat, PackageCheck, Store } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STEPS = [
  { label: "Received", icon: Clock, desc: "We've got your order" },
  { label: "Preparing", icon: ChefHat, desc: "Freshly making it now" },
  { label: "Ready for Pickup", icon: PackageCheck, desc: "Grab it at the lounge" },
];

export default function OrderTracking() {
  const orderNo = new URLSearchParams(window.location.search).get("order") || "";
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!orderNo) { setNotFound(true); return; }
    let active = true;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/orders/track/${orderNo}`);
        if (!active) return;
        setData(res.data);
      } catch {
        if (active) setNotFound(true);
        return;
      }
      setTimeout(() => active && poll(), 4000);
    };
    poll();
    return () => { active = false; };
  }, [orderNo]);

  const stage = data?.stage ?? 0;

  return (
    <div className="min-h-screen bg-noir text-white noise relative py-16 px-5" data-testid="order-tracking">
      <div className="relative z-10 max-w-lg mx-auto">
        <div className="font-display text-xl font-extrabold uppercase tracking-tight">Protein<span className="text-amber-brand">Cafe</span></div>
        <span className="block text-[10px] uppercase tracking-[0.3em] text-white/50 mt-1">The Fuel Lounge™</span>

        {notFound ? (
          <div className="mt-16 text-center" data-testid="track-not-found">
            <h1 className="font-display text-3xl font-bold">Order not found</h1>
            <p className="mt-2 text-white/60">Double-check your order number.</p>
          </div>
        ) : (
          <>
            <div className="mt-10 rounded-3xl border border-white/10 bg-noir-soft p-7">
              <div className="text-xs uppercase tracking-[0.2em] text-amber-brand font-bold">Order</div>
              <div className="font-mono text-3xl font-bold" data-testid="track-order-number">{orderNo}</div>
              {data && <p className="mt-1 text-white/60">Pickup: {data.pickup_time}</p>}

              <div className="mt-8 space-y-6" data-testid="track-steps">
                {STEPS.map((s, i) => {
                  const done = i < stage;
                  const activeStep = i === stage;
                  const Icon = s.icon;
                  return (
                    <div key={s.label} className="flex items-start gap-4" data-testid={`track-step-${i}`} data-state={done ? "done" : activeStep ? "active" : "pending"}>
                      <span className={`grid place-items-center w-11 h-11 rounded-full shrink-0 transition-colors ${done ? "bg-[#DE9A2C] text-[#17120C]" : activeStep ? "bg-[#8A2231] text-white" : "bg-white/5 text-white/40"}`}>
                        {done ? <Check size={20} /> : activeStep ? <Loader2 size={20} className="animate-spin" /> : <Icon size={20} />}
                      </span>
                      <div className={i < STEPS.length - 1 ? "pb-2" : ""}>
                        <div className={`font-display text-xl font-bold ${done || activeStep ? "text-white" : "text-white/40"}`}>{s.label}</div>
                        <div className="text-sm text-white/50">{s.desc}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {stage >= 2 && (
                <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#DE9A2C]/15 text-amber-brand px-4 py-2 text-sm font-bold" data-testid="track-ready">
                  <Store size={16} /> Ready — come collect it!
                </div>
              )}
            </div>

            {data?.items && (
              <div className="mt-6 rounded-3xl border border-white/10 bg-noir-soft p-6">
                <div className="text-xs uppercase tracking-[0.2em] text-white/50 font-bold mb-3">Your items</div>
                {data.items.map((it, idx) => (
                  <div key={idx} className="flex justify-between py-1.5 text-white/80">
                    <span>{it.qty}× {it.name}</span>
                    <span className="font-mono">₹{it.price * it.qty}</span>
                  </div>
                ))}
                <div className="flex justify-between border-t border-white/10 mt-2 pt-3 font-bold">
                  <span>Total</span><span className="font-mono" data-testid="track-total">₹{data.subtotal}</span>
                </div>
              </div>
            )}
          </>
        )}

        <button data-testid="track-home" onClick={() => (window.location.href = "/")} className="mt-8 rounded-full bg-[#DE9A2C] hover:bg-[#c9861f] text-[#17120C] font-bold px-8 py-3 transition-colors">
          Back to ProteinCafe
        </button>
      </div>
    </div>
  );
}
